/**
 * ClientSettingsLayout — the settings shell (S4). Deliberately plain
 * substrate: warm background, one container, a sticky section rail, the
 * header (P1) and the strip (P2) above the section outlet. The rail's
 * active item carries TWO distinctions (coral text + coral left rule, plus
 * font-medium weight); idle items are ink-2 with a hover state.
 *
 * `ClientScope` remounts the outlet whenever the active client changes —
 * one mechanism absorbing every per-page remount hack (the remount-fix class).
 *
 * Strings come from copyTokens (P4). The one-time transition notice is a
 * single dismissible line, flagged in localStorage.
 */

import { useState, type ReactNode } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTenantStore } from '@/stores/useTenantStore';
import { ClientContextHeader } from './ClientContextHeader';
import { SetupStatusStrip } from './SetupStatusStrip';
import { SettingsRail } from './SettingsRail';
import { COPY, SETTINGS_SECTIONS } from '@/lib/copyTokens';

const NOTICE_KEY = 'settings-layout-notice-dismissed';
/** Auto-hide the one-time layout notice after this date even if not dismissed —
 * a removal trigger, not just a dismissal (S8 step 10). Drop the notice entirely
 * (the LayoutNotice component + this constant) once this date passes. */
const NOTICE_EXPIRY = new Date('2026-10-01T00:00:00Z').getTime();

function readNoticeDismissed(): boolean {
  if (Date.now() > NOTICE_EXPIRY) return true;
  try {
    return window.localStorage.getItem(NOTICE_KEY) === '1';
  } catch {
    return true; // storage unavailable → don't nag
  }
}

/**
 * Remounts children when the active client changes, so client-scoped local
 * state (tabs, filters, forms) can never bleed across clients.
 */
export function ClientScope({ tenantId, children }: { tenantId: string | null; children: ReactNode }) {
  return (
    <div key={tenantId ?? 'none'} className="w-full min-w-0">
      {children}
    </div>
  );
}

function SettingsSectionNav() {
  // Content Preferences remains visible when locked so users can understand
  // the capability and its readiness state; the page itself prevents edits.
  return <SettingsRail items={SETTINGS_SECTIONS} ariaLabel={COPY.sectionsAriaLabel} />;
}

function LayoutNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-warm-border bg-paper-2 px-3 py-2 text-xs text-ink-2">
      <span>{COPY.layoutNotice}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 font-medium text-ink-2 hover:text-ink"
      >
        {COPY.layoutNoticeDismiss}
      </button>
    </div>
  );
}

export function ClientSettingsLayout() {
  const tenantId = useTenantStore((s) => s.currentTenant);
  const [noticeDismissed, setNoticeDismissed] = useState(readNoticeDismissed);

  const dismissNotice = () => {
    try {
      window.localStorage.setItem(NOTICE_KEY, '1');
    } catch {
      /* non-fatal */
    }
    setNoticeDismissed(true);
  };

  return (
    <div className="min-h-full bg-warm-bg">
      <div className="w-full">
        {!noticeDismissed ? <LayoutNotice onDismiss={dismissNotice} /> : null}
        {tenantId ? (
          <>
            <div className="mx-auto w-full max-w-7xl space-y-6">
              <ClientContextHeader />
              <SetupStatusStrip />
              <SettingsSectionNav />
              <ClientScope tenantId={tenantId}>
                <Outlet />
              </ClientScope>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-warm-border bg-paper px-6 py-10 text-center">
            <p className="text-sm text-ink-2">{COPY.chooseEntity}</p>
            <Link
              to="/create-workspace"
              className="mt-3 inline-block text-sm font-medium text-coral hover:text-coral-hover"
            >
              {COPY.addEntity}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
